/**
 * @module lib/commercial/dfp-ads/dynamic-ad
 * @description Dynamically create a single Ad slot.
 * This file assumes that both `gpt.js` were already loaded on page load.
 * Refer to paired file for additional documentation.
 * @see module:lib/commercial/dfp-ads/index
 */
import includes from "lodash/includes";
import isNil from "lodash/isNil";

import tgamConfig from "config/tgam";
import { getCreativeIdForAdvert } from "lib/commercial/ad-sequencing";
import { adServerReady, displayAdSlots } from "lib/commercial/dfp-ads/header-bidding";
import { getMoatViewability } from "lib/commercial/moat/viewability";
import ee from "lib/eventemitter";
import { mark, measure } from "lib/performance/user-timing";

import Deferred from "utils/Deferred";
import { isTablet, isDesktop } from "utils/detect";
import logger from "utils/logger";
import { getUrlVars } from "utils/urls";
import { isFunctionThenInvoke, parseDataSetObject, toArray, getProp } from "utils/utils";
import {
  WIDTHS,
  buildCommonMapping,
  buildCustomMapping,
  buildRefreshMapping,
  getViewportBreakpoint,
  getViewportTier
} from "./adslot-size-mapping";
import {
  gptSlotCreate,
  gptSlotRefresh,
  gptSlotDisplay,
  gptSlotDestroy,
  gptSlotEvent,
  gptSizeMapping
} from "./gpt-ads";

// Header bidding-related settings
const hbAPSTagEnabled = getProp(tgamConfig, "env.hbAPSTagEnabled", false);
const hbPrebidEnabled = getProp(tgamConfig, "env.hbPrebidEnabled", false);
const hbLazyAdsEnabled = getProp(tgamConfig, "env.hbLazyAdsEnabled", false);
const hbRefreshingAdsEnabled = getProp(tgamConfig, "env.hbRefreshingAdsEnabled", false);

const debugTag = "[DFP-ADS]";
const debugClock = !isNil(getUrlVars().clock); // extra debug info for refreshing

// (ʘ‿ʘ) HOLA, here is an OLOO pattern.
export default {
  /**
   * @public
   * @method init
   * @description initialize ad
   * @param {Object} c - collection of properties
   * @param {String} c.id - of the HTML element
   * @param {Boolean} [c.cloned=false] - is this an infinitely-repeating dynamic ad?
   * @param {Boolean} [c.immediate=true] - load the ad now or lazy-load it later?
   * @param {Boolean} [c.deferImmediate=false] - instead of allowing the ad to display itself, resolve a Promise when the ad is ready to display, so that external code can decide when to display it.
   * @param {Number} [c.refresh=0] - how many times an ad should try to refresh itself.
   * @param {Number} [c.interval=32] - how long an ad should display (in seconds) before refreshing itself.
   * @param {Number} [c.sizemap=null] - optionally set a custom size mapping, otherwise determine based on id.
   * @param {String} [c.unitpath=""] - the full path of the ad unit with the network code and unit code.
   * @param {Function} [c.onRendered=null] - callback method triggered after ad slot is rendered.
   */
  init({
    id,
    cloned = false,
    immediate = true,
    deferImmediate = false,
    refresh = 0,
    interval = 32,
    sizemap = null,
    unitpath = "",
    onRendered = null
  }) {
    mark("dfp_ad_init_started");

    const element = document.getElementById(id);
    if (this.element || !element) {
      mark("dfp_ad_init_done");
      measure("dfp_ad_init");
      return; // Already initialized or not found
    }
    this.id = id; // string - unique identifier (dom element id)
    this.cloned = cloned;
    this.element = element; // element - of the ad slot
    this.immediate = this.element.dataset.immediate === "true" || immediate; // boolean - immediate-load or lazy-load
    this.deferImmediate = this.immediate && deferImmediate; // boolean - allow external code to decide when the ad will be displayed
    this.deferImmediateReady = this.deferImmediate ? new Deferred() : Promise.resolve(); // promise
    this.type = this.element.dataset.type; // string - boxr, ldbd, halfpager, etc.
    this.slot = null; // googletag.slot - ad DFP GPT slot
    this.state = "ready"; // enum: ready, loading, rendered, empty

    this.lastSize = []; // array - last successfully rendered Ad size mapping (used for refresh calls)
    this.refresh = refresh; // number - track how many times an Ad can still be refreshed (0 means don't refresh)
    this.interval = interval; // number - refresh ad after displayed for this many seconds in viewport.
    this.clock = 0; // number - seconds the Ad has been in viewport (since last refresh)

    // determine whether this ad is eligible for header bidding
    this.prebid = true;
    if ((!hbPrebidEnabled && !hbAPSTagEnabled) || (!this.immediate && !hbLazyAdsEnabled) || (this.refresh > 0 && !hbRefreshingAdsEnabled)) {
      this.prebid = false;
    }
    this.adObserver = null; // IntersectionObserver - for auto-refreshing
    this.displayObserver = null; // IntersectionObserver - for initial lazy-loading

    this.isIntersecting = false; // boolean - true is ad is within the viewport
    this.intersectionRatio = 0; // number - percentage of ad visible within the viewport, between 0 and 1.
    this.activeViewRatio = 0.5; // percentage of ad in viewport, that is considered "active view".

    this.targets = parseDataSetObject(this.element.dataset.target); // e.g. { pos: "flex2", ptf: "gpt" }
    this.targets.refresh = "false"; // set to false by default (ARC-4470)
    this.targets.ugc = "f"; // user generated content (ARC-5514)

    this.refreshCount = 0;
    this.sizemap = sizemap; // true dynamic ads pass a custom size mapping
    this.sizemapForTier = null;
    this.unitpath = unitpath; // full path of the ad unit with the network code and unit code.
    this.onRendered = onRendered; // pass on the event, if a callback method is defined.

    // We need a sizemap to pass to Prebid, but it doesn't need to be in GPT
    // "googletag.SizeMappingArray" format yet
    if (this.sizemap) {
      this.sizemap = buildCustomMapping(this.sizemap);
    } else {
      this.sizemap = buildCommonMapping(this.id, tgamConfig.meta, this.cloned);
    }
    if (this.sizemap.length === 0) {
      this.destroy();
    } else {
      // determine the appropriate size mapping for the current breakpoint
      this.sizemapForTier = this.getSizeMapForTier(getViewportTier());
      adServerReady.then(this.onAdServerReady.bind(this));
    }
    mark("dfp_ad_init_done");
    measure("dfp_ad_init");
  },

  /**
   * Get the intersection observer root margin for various types of pages.
   * @returns {Number} - the root margin
   */
  getRootMargin() {
    const config = JSON.parse(getProp(tgamConfig, "env.adRootMarginConfig", "{'story': 2.25, 'other': 2, 'stock': 1, 'fund': 1, 'index': 1, 'watchlist': 1, 'portfolio': 1}"));
    const pageType = getProp(tgamConfig, "meta.pagetype", "");
    const path = window.location.pathname;
    let key = "other";
    if (pageType === "story") {
      key = "story";
    }
    if (/markets\/stocks\//gim.test(path)) {
      // stocks and etfs
      key = "stock";
    }
    if (/markets\/funds\//gim.test(path)) {
      key = "fund";
    }
    if (/markets\/indices\//gim.test(path)) {
      key = "index";
    }
    if (/markets\/watchlist\//gim.test(path)) {
      key = "watchlist";
    }
    if (/markets\/portfolio\//gim.test(path)) {
      key = "portfolio";
    }
    // allows for whole numbers (1) and floats (0.25, 2.25) in config
    return parseFloat(config[key]);
  },

  /**
   * @method getSizeMapForTier
   * @description Extract the sizemap corresponding to the current breakpoint
   * @param {String} tier
   * @returns {Array}
   */
  getSizeMapForTier(tier) {
    return this.sizemap.find(sm => {
      let match = false;
      Object.keys(WIDTHS).forEach(tierName => {
        if (tierName === tier && WIDTHS[tierName] === sm.minWidth) {
          match = true;
        }
      });
      return match;
    });
  },

  /**
   * @method onAdServerReady
   * @description Finish the init after GPT API is ready.
   */
  onAdServerReady() {
    mark("dfp_ad_ready_started");
    logger.groupCollapsed(`${debugTag} gpt slot ${this.id}`);

    // Convert the sizemap array into GPT "googletag.SizeMappingArray" format
    const originalSizemap = this.sizemap;
    this.sizemap = gptSizeMapping(this.sizemap);

    if (this.sizemap.length === 0) {
      mark("dfp_ad_ready_done");
      measure("dfp_ad_ready");
      this.destroy();
      logger.groupEnd(`${debugTag} gpt slot ${this.id}`);
      return;
    }
    const bp = getViewportBreakpoint(); // current viewport tier breakpoint integer
    const tier = getViewportTier();

    // conditional CID - if creatives are present and match is found for ad unit
    const cid = getCreativeIdForAdvert(this.id, this.sizemap, bp);
    if (cid) {
      this.targets.cid = cid;
      // If specific creative has already been assigned to this ad slot,
      // (e.g. via ad sequencing) then we shouldn't request bids for it
      this.prebid = false;
    }
    logger.info(`${debugTag} slot:`, this);
    logger.info(`${debugTag} all sizes (original format):`, originalSizemap);
    logger.info(`${debugTag} all sizes (GPT format):`, this.sizemap);
    logger.info(`${debugTag} sizes for "${tier}" tier:`, this.sizemapForTier);

    this.targets.lz = !this.immediate; // lazy-loaded indicator
    if (tgamConfig.env.moatViewability) { // add moat viewability, if enabled
      this.targets.in_view_perc = getMoatViewability(this.id, this.targets.pos);
    }
    this.processTargets();

    // event listeners
    this.setRenderListener();
    if (this.refresh > 0) {
      this.setAdObserver();
      this.setVisibilityListener();
    }
    // Create the slot
    this.slot = gptSlotCreate(this.id, this.unitpath, this.sizemap);

    // Apply the slot's page targets
    Object.keys(this.targets).forEach(k => {
      this.slot.setTargeting(k, this.targets[k]);
    });

    // Handle ad display
    if (this.deferImmediate) {
      // Instead of initiating a separate request for each Prebid-eligible ad,
      // it's better to batch all of them into a single request, which is why
      // we're allowing outside code to handle this for the DynamicAd instance
      logger.info(`${debugTag} display: immediate (batched)`);
      logger.groupEnd(`${debugTag} gpt slot ${this.id}`);
      this.deferImmediateReady.resolve();

    } else if (this.immediate && !this.deferImmediate) {
      logger.info(`${debugTag} display: immediate`);
      logger.groupEnd(`${debugTag} gpt slot ${this.id}`);
      displayAdSlots([this]);

    } else {
      logger.info(`${debugTag} display: lazy`);
      logger.groupEnd(`${debugTag} gpt slot ${this.id}`);
      this.setDisplayObserver();
    }
    mark("dfp_ad_ready_done");
    measure("dfp_ad_ready");
  },

  /**
   * @method processTargets
   * @description Apply some custom mapping to target values, before assigning them to the ad slot.
   */
  processTargets() {
    Object.keys(this.targets).forEach(k => {
      if (k == "pos" && includes(this.targets[k], ",")) {
        // Split the 'pos' value into array items
        let allPositions = this.targets[k].split(",");
        // The mobile custom position field is used as the default
        let position = allPositions[0];

        if (isTablet() && !isNil(allPositions[1])) {
          // If the tablet custom position field is populated, and this is the tablet breakpoint, use that
          position = allPositions[1];
        } else if (isDesktop() && !isNil(allPositions[2])) {
          // If the desktop custom position field is populated, and this is the desktop breakpoint, use that
          position = allPositions[2];
        }
        logger.info(`${debugTag} all positions:`, allPositions);
        this.targets.pos = position;
      }
      logger.info(`${debugTag} ${k}:`, this.targets[k]);
    });
  },

  /**
   * Update/add a page target for the ad slot
   * @param {String} key - targeting param key
   * @param {String|Array<String>} value - targeting param value
   */
  updateTarget(key, value) {
    this.slot.setTargeting(key, value);
  },

  /**
   * @method setRenderListener
   * @description Add event listener for when the ad slot renders.
   * @see https://developers.google.com/doubleclick-gpt/reference#googletageventsslotrenderendedevent
   */
  setRenderListener() {
    gptSlotEvent(this.id, "slotRenderEnded", event => {
      if (event.isEmpty) {
        this.destroy();
      } else {
        this.state = "rendered";
        this.lastSize = event.size; // need for refresh calls
        // if we don't check for parent presence, then an error will occur if a paywall/regwall is displayed
        const hasGrandparent = !isNil(this.element.parentNode) && !isNil(this.element.parentNode.parentNode);
        if (hasGrandparent) {
          this.element.parentNode.parentNode.dataset.size = event.size.join("x");
        }
      }
      isFunctionThenInvoke(this.onRendered, [event, this.id]);
    });
  },

  /**
   * @method setVisibilityListener
   * @description Watch the visibilty of the current browser tab.
   * @see https://developer.mozilla.org/en-US/docs/Web/API/Page_Visibility_API
   */
  setVisibilityListener() {
    this.boundVisibilitychange = this.onVisibilitychange.bind(this);
    document.addEventListener("visibilitychange", this.boundVisibilitychange, false);
  },

  /**
   * @method onVisibilitychange
   * @description Start or stop the timer, if the tab/document is hidden or not.
   * @see https://developer.mozilla.org/en-US/docs/Web/API/Document/hidden
   */
  onVisibilitychange() {
    if (document.hidden) {
      this.stopTimer();
    } else if (this.isActiveView()) {
      this.startTimer();
    }
  },

  /**
   * @method isActiveView
   * @description True if 50% of the ad’s pixels are visible in the browser viewport.
   * @returns {Boolean}
   */
  isActiveView() {
    return this.isIntersecting && this.intersectionRatio > this.activeViewRatio;
  },

  /**
   * @method setAdObserver
   * @description Use Intersection Observer help refresh the Ad
   * @see https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API
   */
  setAdObserver() {
    this.adObserver = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        this.isIntersecting = entry.isIntersecting;
        this.intersectionRatio = entry.intersectionRatio;

        if (this.isActiveView()) {
          // Delay startTimer() for 1 second
          debugClock && logger.info(`${debugTag} delaying startTimer() on ${this.id} for 1 second`);
          setTimeout(() => {
            debugClock && logger.info(`${debugTag} adObserver in:`, this.id, entry.intersectionRatio);
            this.startTimer();
          }, 1000);

        } else {
          debugClock && logger.info(`${debugTag} adObserver out:`, this.id);
          this.stopTimer();
        }
      });
    }, {
      root: null, // browser viewport
      rootMargin: "0px", // no margin on viewport
      threshold: [0.0, this.activeViewRatio] // percentage values
    });
    this.adObserver.observe(this.element);
  },

  /**
   * @method setDisplayObserver
   * @description Use Intersection Observer to lazy-load the Ad.
   * @see https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API
   */
  setDisplayObserver() {
    const rootMarginPercentage = `${this.getRootMargin() * 100}%`;
    logger.info(`${debugTag} root margin percentage`, rootMarginPercentage);
    this.displayObserver = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          logger.info(`${debugTag} setDisplayObserver: `, this);
          debugClock && logger.info(`${debugTag} displayObserver:`, this.id);
          this.lazyDisplay();
        }
      });
    }, {
      root: null, // browser viewport
      rootMargin: rootMarginPercentage,
      threshold: [0.0] // as soon as it crosses the margin
    });
    this.displayObserver.observe(this.element);
  },

  /**
   * @method startTimer
   * @description Increment the clock every second until the interval is hit.
   */
  startTimer() {
    let userIdle = false;
    let userInteracting = false;
    let expandableAdElExists = false;
    if (this.refresh <= 0) {
      return;
    }
    this.stopTimer(); // prevent duplicate instances
    debugClock && logger.info(`${debugTag} startTimer`, this.id);

    ee.on("userTimer:idle", () => {
      userIdle = true;
    });

    const userIsActive = () => {
      userIdle = false;
    };

    ee.on("windowScroll.throttled", userIsActive);
    ee.on("mouseMove.throttled", userIsActive);
    ee.on("ad:over", () => {
      userInteracting = true;
      debugClock && logger.info(`${debugTag} User is over ad slot, pausing clock`, this.id);
    });
    ee.on("ad:out", () => {
      userInteracting = false;
      debugClock && logger.info(`${debugTag} User is out of ad slot, resuming clock`, this.id);
    });

    let adSlotType = this.type;
    let expandableAdElements = toArray(document.querySelectorAll("[class^=expandable-ad-]"));
    expandableAdElements.forEach(el => {
      let expandableAdType = el.className.split("expandable-ad-")[1];
      if (expandableAdType === adSlotType) {
        expandableAdElExists = true;
      }
    });

    this.timer = setInterval(() => {
      let incrementClock = !userIdle && !userInteracting && this.isActiveView() && !expandableAdElExists;
      if (incrementClock) {
        this.clock += 1;
      }
      debugClock && logger.info(`${debugTag} clock:`, this.id, this.clock);
      if (this.clock >= this.interval) {
        this.prepareToRefreshSlot();
      }
      if (this.refresh <= 0) {
        this.stopTimer();
      }
    }, 1000); // ~ 1 second (approx)
  },

  /**
   * @method stopTimer
   * @description Stop the clock, until we want to start it again.
   */
  stopTimer() {
    clearInterval(this.timer);
    debugClock && logger.info(`${debugTag} stopTimer:`, this.id, this.refresh);
  },

  /**
   * @method lazyDisplay
   * @description Attempt to lazy load a single ad poistion if in the viewport.
   **/
  lazyDisplay() {
    if (this.state === "ready" && !this.element.classList.contains("u-hidden")) {
      this.displayObserver.disconnect();
      logger.info(`${debugTag} display slot (lazy):`, this.id);
      // Calling this function will ultimately result in "displaySlot()" being called
      displayAdSlots([this]);
    }
  },

  /**
   * @method displaySlot
   * @description Immediately display a single GPT ad slot
   */
  displaySlot() {
    this.state = "loading";
    gptSlotDisplay(this.slot);
  },

  /**
   * @method refreshSlot
   * @description Immediately refresh a single GPT ad slot
   */
  refreshSlot() {
    this.state = "loading";
    gptSlotRefresh(this.slot, { changeCorrelator: true });
  },

  /**
   * @method prepareToRefreshSlot
   * @description Determine if the Ad should now be refreshed (again).
   */
  prepareToRefreshSlot() {
    this.clock = 0;
    this.refresh -= 1;
    this.refreshCount += 1;
    logger.info(`${debugTag} refreshed:`, this.id, this.refresh);
    if (this.slot && this.state == "rendered") {
      this.slot.setTargeting("refresh_count", this.refreshCount);
      // the same size mapping will be used for all refresh calls
      if (this.targets.refresh === "false") {
        this.targets.refresh = "true";
        const smap = gptSizeMapping(buildRefreshMapping(this.lastSize));
        this.slot.defineSizeMapping(smap);
        this.slot.setTargeting("refresh", "true");
      }
      displayAdSlots([this], true);
    }
  },

  /**
   * @public
   * @method destroy
   * @description Since this is a dynamic Ad, you should remove it from the DOM eventually.
   */
  destroy() {
    this.state = "empty"; // prevent ad loading
    this.refresh = 0; // prevent timer restart
    this.stopTimer();
    if (this.slot) {
      gptSlotDestroy(this.slot);
    }
    if (this.element) {
      const adParent = this.element.closest(".c-ad");
      if (adParent) {
        // try to remove relative parent ad element (grey box) which has padding/margin
        adParent.parentNode.removeChild(adParent);
      } else {
        // otherwise just remove immediate parent
        this.element.parentNode.removeChild(this.element);
      }
    }
    if (this.displayObserver) {
      this.displayObserver.disconnect();
    }
    if (this.adObserver) {
      this.adObserver.disconnect();
    }
    if (this.boundVisibilitychange) {
      document.removeEventListener("visibilitychange", this.boundVisibilitychange, false);
    }
  },
};
